import React from 'react';
import PropTypes from 'prop-types';
import styles from './ModalParts.module.scss';

/* ────────────────────────────────────────────────────────────
 * ModalSection — token-bordered card that groups a few related
 * fields inside a modal body.
 *
 * Use one per logical group of fields when the form has more
 * than ~4 fields total. Each section has a soft-tinted icon
 * tile, a title, and a one-line meta hint above its children.
 *
 *   <ModalSection
 *     icon={<GitBranch size={16} strokeWidth={1.75} />}
 *     title="Basics"
 *     meta="Name this version and choose what to base it on."
 *   >
 *     <ModalGrid>
 *       <ModalField label="Menu name" required> ... </ModalField>
 *       <ModalField label="Clone from"> ... </ModalField>
 *     </ModalGrid>
 *   </ModalSection>
 *
 * `tone` swaps the icon tile background to match the section's
 * mood (info for neutral metadata, warning for caution, etc.).
 * ──────────────────────────────────────────────────────────── */

const TONE_MAP = {
  info: styles.toneInfo,
  success: styles.toneSuccess,
  warning: styles.toneWarning,
  danger: styles.toneDanger,
  neutral: styles.toneNeutral,
};

const ModalSection = ({ icon, title, meta, tone, children, className }) => (
  <section
    className={[styles.section, className || ''].filter(Boolean).join(' ')}
  >
    {(icon || title || meta) && (
      <header className={styles.sectionHead}>
        {icon && (
          <span
            className={[styles.sectionIcon, TONE_MAP[tone] || TONE_MAP.info]
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          >
            {icon}
          </span>
        )}
        <span className={styles.sectionTitleWrap}>
          {title && <span className={styles.sectionTitle}>{title}</span>}
          {meta && <span className={styles.sectionMeta}>{meta}</span>}
        </span>
      </header>
    )}
    {children}
  </section>
);

ModalSection.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node,
  meta: PropTypes.node,
  tone: PropTypes.oneOf(['info', 'success', 'warning', 'danger', 'neutral']),
  children: PropTypes.node,
  className: PropTypes.string,
};

ModalSection.defaultProps = {
  icon: null,
  title: null,
  meta: null,
  tone: 'info',
  children: null,
  className: '',
};

export default ModalSection;
